import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import { ClipLoader } from "react-spinners";
import * as constants from "../../../../../constants";
import fetch from "../../../../util/api-ajax";

// Telemetry is kept for 7 days; past ~5 days the oldest events lose their start SOC.
const WINDOW_OPTIONS = [
  { hours: 24, label: "Last 24 hours", span: "24 hours" },
  { hours: 72, label: "Last 3 days", span: "3 days" },
  { hours: 120, label: "Last 5 days", span: "5 days" },
];
const DEFAULT_WINDOW_HOURS = 72;
const HOUR_MS = 3600 * 1000;
// A gap between chart points longer than this is a telemetry outage: the line breaks
// there instead of drawing a straight line across it.
const CHART_GAP_MS = 15 * 60 * 1000;

const CHART_W = 1000;
const CHART_H = 90;

const GREEN = "#2e7d32";

const EVENT_STYLE = {
  CHARGE: { label: "Charge", color: "#2f5fc4", background: "#e6eefb" },
  DISCHARGE: { label: "Discharge", color: "#b8641c", background: "#fcf0e1" },
};

const typeBadgeStyle = (code) => ({
  display: "inline-block",
  // One width for every type, so Charge and Discharge line up.
  width: "96px",
  textAlign: "center",
  boxSizing: "border-box",
  padding: "4px 0",
  borderRadius: "4px",
  fontWeight: 500,
  fontSize: "14px",
  color: (EVENT_STYLE[code] || {}).color || "#555",
  backgroundColor: (EVENT_STYLE[code] || {}).background || "#eee",
});

const windowSelectStyle = {
  padding: "6px 14px",
  fontSize: "14px",
  fontWeight: 600,
  color: "#3a4fc4",
  background: "#eef0fb",
  border: "1px solid #c9cef0",
  borderRadius: "18px",
  cursor: "pointer",
};

const cellStyle = { padding: "12px 14px", borderBottom: "1px solid #eee", fontSize: "14px", whiteSpace: "nowrap" };
const headStyle = {
  ...cellStyle,
  fontSize: "13px",
  color: "#444",
  fontWeight: 700,
  textAlign: "left",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  borderBottom: "2px solid #e2e2e2",
};

// Both markets (ERCOT, ComEd) run on Central time and Sentinel shows every
// timestamp in it, so times are Central regardless of the viewer's browser —
// labelled CT so remote viewers are not misled.
const TZ = "America/Chicago";

// "Sep 7, 6:50pm", as in the design.
const fmtTime = (t) =>
  new Date(t)
    .toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: TZ })
    .replace(/\s?(AM|PM)$/, (m, ap) => ap.toLowerCase());

const localHour = (ms) =>
  Number(new Date(ms).toLocaleString("en-US", { hour: "numeric", hourCycle: "h23", timeZone: TZ }));

// Central midnights inside the window, found by walking whole hours so DST changes
// land on the right boundary.
const localMidnights = (windowStart, windowEnd) => {
  const out = [];
  for (let ms = Math.ceil(windowStart / HOUR_MS) * HOUR_MS; ms < windowEnd; ms += HOUR_MS) {
    if (localHour(ms) === 0) out.push(ms);
  }
  return out;
};

const fmtDuration = (startIso, endIso) => {
  const mins = Math.round((new Date(endIso) - new Date(startIso)) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

// The device's last-known state from its latest telemetry snapshot — the same fields
// Sentinel shows — so a stale or offline device reads as such, not as a broken view.
const LastKnown = ({ device }) => {
  if (!device) return null;
  const parts = [];
  if (device.last_reported_time) parts.push(`last seen ${fmtTime(device.last_reported_time)} CT`);
  if (device.command_status) parts.push(`last reported mode: ${device.command_status}`);
  if (device.online_status) parts.push(device.online_status);
  if (!parts.length) return null;
  return <div style={{ marginTop: "4px", color: "#666" }}>Device {parts.join(" · ")}</div>;
};

const fmtPct = (v) => `${Math.round(v)}%`;

// "98% → 47% (-51%)", as in the design, from the averaged readings at the event's
// real start and end.
const socChange = (e) => {
  if (!e.has_soc_start || !e.has_soc_end) {
    const known = e.has_soc_start ? `from ${fmtPct(e.soc_start_pct)}` : e.has_soc_end ? `to ${fmtPct(e.soc_end_pct)}` : "";
    return `No reading near ${e.has_soc_start ? "end" : e.has_soc_end ? "start" : "start or end"}${known ? ` (${known})` : ""}`;
  }
  const delta = Math.round(e.soc_end_pct) - Math.round(e.soc_start_pct);
  return `${fmtPct(e.soc_start_pct)} → ${fmtPct(e.soc_end_pct)} (${delta > 0 ? "+" : ""}${delta}%)`;
};

const axisLabelStyle = { position: "absolute", fontSize: "11px", color: "#999", pointerEvents: "none" };

const SocChart = ({ points, windowStart, windowEnd }) => {
  const span = windowEnd - windowStart;
  const x = (ms) => ((ms - windowStart) / span) * CHART_W;
  const y = (pct) => (1 - Math.min(Math.max(pct, 0), 100) / 100) * CHART_H;

  const segments = [];
  let current = [];
  let prev = null;
  points.forEach((p) => {
    // A point without a SOC breaks the line rather than dropping it to 0%.
    if (p.soc_pct == null) {
      if (current.length) segments.push(current);
      current = [];
      prev = null;
      return;
    }
    const ms = new Date(p.bucket).getTime();
    if (prev !== null && ms - prev > CHART_GAP_MS && current.length) {
      segments.push(current);
      current = [];
    }
    current.push(`${x(ms).toFixed(1)},${y(p.soc_pct).toFixed(1)}`);
    prev = ms;
  });
  if (current.length) segments.push(current);

  const midnights = localMidnights(windowStart, windowEnd);

  // Labels are HTML over the stretched SVG so the text is not distorted.
  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" style={{ width: "100%", height: `${CHART_H}px`, display: "block", overflow: "visible" }}>
        {[0, 50, 100].map((pct) => (
          <line key={pct} x1={0} x2={CHART_W} y1={y(pct)} y2={y(pct)} stroke="#e6e6e6" strokeWidth={pct === 0 ? 2 : 1} vectorEffect="non-scaling-stroke" />
        ))}
        {midnights.map((ms) => (
          <line key={ms} x1={x(ms)} x2={x(ms)} y1={0} y2={CHART_H} stroke="#ccc" strokeDasharray="3,3" vectorEffect="non-scaling-stroke" />
        ))}
        {segments.map((s, i) => (
          <polyline key={i} points={s.join(" ")} fill="none" stroke={GREEN} strokeWidth={2} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <span style={{ ...axisLabelStyle, left: "8px", top: "4px" }}>100%</span>
      <span style={{ ...axisLabelStyle, left: "8px", bottom: "4px" }}>0%</span>
      {midnights.map((ms) => (
        <span key={ms} style={{ ...axisLabelStyle, top: "4px", left: `calc(${(x(ms) / CHART_W) * 100}% + 6px)` }}>
          {new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: TZ })}
        </span>
      ))}
    </div>
  );
};

// CommandHistoryDialog shows one battery's charge/discharge activity over a recent
// window (3 days by default): a SOC chart for shape, and every event with its real
// start/end SOC, newest first. Fetched live each time it opens or the window changes.
const CommandHistoryDialog = ({ open, device, onClose }) => {
  const deviceId = device && device.device_id;
  const [hours, setHours] = useState(DEFAULT_WINDOW_HOURS);
  const [state, setState] = useState({ loading: false, error: null, data: null, fetchedAt: null });

  useEffect(() => {
    if (!open || !deviceId) return undefined;
    let cancelled = false;
    setState({ loading: true, error: null, data: null, fetchedAt: null });
    fetch(constants.DEVICE_COMMAND_HISTORY_URL, { device_id: deviceId, hours })
      .then((res) => {
        if (!cancelled) setState({ loading: false, error: null, data: res || {}, fetchedAt: Date.now() });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: (err && err.message) || "Unexpected error", data: null, fetchedAt: null });
      });
    return () => {
      cancelled = true;
    };
  }, [open, deviceId, hours]);

  // Reset the window on close, so the next open starts at the default with a
  // single fetch.
  const handleClose = () => {
    setHours(DEFAULT_WINDOW_HOURS);
    onClose();
  };

  const { loading, error, data, fetchedAt } = state;
  // Newest first, without relying on the order the API returns.
  const events = [...((data && data.events) || [])].sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
  const points = (data && data.soc) || [];
  const windowEnd = fetchedAt || Date.now();
  const windowStart = windowEnd - hours * HOUR_MS;
  const windowSpan = WINDOW_OPTIONS.find((o) => o.hours === hours).span;
  // meta_data stores city upper-case ("SOUTHLAKE"); the design shows "Southlake".
  const city = ((data && data.city) || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  const location = data ? [city, data.settlement_point].filter(Boolean).join(", ") : "";

  return (
    <Dialog maxWidth="md" fullWidth onClose={handleClose} open={open}>
      <DialogTitle disableTypography style={{ paddingBottom: "20px" }}>
        <div style={{ fontSize: "24px", fontWeight: "bold", color: "#222", marginBottom: "14px" }}>Command History</div>
        <select
          aria-label="Time window"
          value={hours}
          onChange={(e) => setHours(Number(e.target.value))}
          disabled={loading}
          style={windowSelectStyle}
        >
          {WINDOW_OPTIONS.map((o) => (
            <option key={o.hours} value={o.hours}>{o.label}</option>
          ))}
        </select>
      </DialogTitle>
      <DialogContent dividers style={{ paddingTop: "20px" }}>
        <div style={{ marginBottom: "12px", fontSize: "15px" }}>
          <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#222" }}>{deviceId}</span>
          {location && <span style={{ color: "#888", marginLeft: "8px" }}>({location})</span>}
        </div>
        {loading && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <ClipLoader size={30} color="#3f51b5" />
          </div>
        )}
        {error && <div style={{ color: "#c62828", padding: "12px 0" }}>Failed to load command history: {error}</div>}
        {data && (
          <div>
            {points.length ? (
              <div>
                <SocChart points={points} windowStart={windowStart} windowEnd={windowEnd} />
                <div style={{ fontSize: "12px", color: "#999", margin: "8px 0 20px" }}>
                  SOC over the same {windowSpan} — for shape/context only; exact values are in the list below
                </div>
              </div>
            ) : (
              <div style={{ fontSize: "13px", color: "#888", margin: "8px 0 16px" }}>
                No telemetry reported in the last {windowSpan}.
                {!events.length ? null : <LastKnown device={device} />}
              </div>
            )}
            {events.length ? (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headStyle}>Type</th>
                    <th style={headStyle}>Start (CT)</th>
                    <th style={headStyle}>End (CT)</th>
                    <th style={headStyle}>Duration</th>
                    <th style={headStyle}>SOC Change</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={`${e.cmd_code}-${e.start_time}`}>
                      <td style={cellStyle}>
                        <span style={typeBadgeStyle(e.cmd_code)}>{(EVENT_STYLE[e.cmd_code] || {}).label || e.cmd_code}</span>
                      </td>
                      <td style={cellStyle}>{fmtTime(e.start_time)}</td>
                      <td style={cellStyle}>{e.in_progress ? "In progress" : fmtTime(e.end_time)}</td>
                      <td style={cellStyle}>
                        {fmtDuration(e.start_time, e.end_time)}
                        {e.in_progress ? " so far" : ""}
                      </td>
                      <td
                        style={{
                          ...cellStyle,
                          fontWeight: "bold",
                          color: e.has_soc_start && e.has_soc_end ? GREEN : "#888",
                        }}
                      >
                        {socChange(e)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: "13px", color: "#888" }}>
                No charge or discharge commands in the last {windowSpan}.
                <LastKnown device={device} />
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CommandHistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  device: PropTypes.shape({
    device_id: PropTypes.string,
    last_reported_time: PropTypes.string,
    command_status: PropTypes.string,
    online_status: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

export default CommandHistoryDialog;
